// src/components/usuario/NoticiasUsuario.jsx
import React, { useEffect, useState, useMemo } from "react";
import api, { API_BASE } from "../../../../api";
import { ENDPOINTS } from "../../Utils/Endpoints";
import { getSessionUserId } from "../../../../utils/SessionUser";

// Helper para construir la URL completa de la imagen
const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http") || path.startsWith("data:")) return path;
    const baseUrl = API_BASE.replace(/\/api\/?$/, ""); // Remover /api del final
    return `${baseUrl}${path}`;
};

// Placeholder image
const PLACEHOLDER_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='250' viewBox='0 0 400 250'%3E%3Crect fill='%23E5E7EB' width='400' height='250'/%3E%3Cpath fill='%239CA3AF' d='M163 142.5l24-24 24 24 48-48 48 48V187H163v-44.5zm48-39.5c-8.3 0-15-6.7-15-15s6.7-15 15-15 15 6.7 15 15-6.7 15-15 15z'/%3E%3C/svg%3E";

const MESES_ABREV = [
    "Ene", "Feb", "Mar", "Abr", "May", "Jun",
    "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
];

const formatFechaCorta = (fechaStr) => {
    if (!fechaStr) return "";
    const d = new Date(fechaStr);
    if (Number.isNaN(d.getTime())) return fechaStr;
    const dia = String(d.getDate()).padStart(2, "0");
    const mes = MESES_ABREV[d.getMonth()] ?? "";
    const anio = d.getFullYear();
    return `${dia} ${mes} ${anio}`;
};

const getCategoriaStyles = (categoria) => {
    const cat = (categoria || "").toLowerCase();
    if (cat.includes("capacita")) return { bg: "#FFEDD5", text: "#9A3412", border: "#F97316" };
    if (cat.includes("rh") || cat.includes("recursos")) return { bg: "#F3E8FF", text: "#6B21A8", border: "#8B5CF6" };
    if (cat.includes("finanza")) return { bg: "#DCFCE7", text: "#166534", border: "#10B981" };
    if (cat.includes("auditor")) return { bg: "#FEE2E2", text: "#991B1B", border: "#EF4444" };
    if (cat.includes("cumple") || cat.includes("birthday")) return { bg: "#FCE7F3", text: "#9D174D", border: "#F472B6" };
    if (cat.includes("festiv") || cat.includes("holiday")) return { bg: "#CCFBF1", text: "#0F766E", border: "#14B8A6" };
    return { bg: "#F1F5F9", text: "#475569", border: "#94A3B8" }; // Default "General"
};

const NoticiasUsuario = ({ onVolver, initialItem }) => {
    const [noticias, setNoticias] = useState([]);
    const [noticiaSeleccionada, setNoticiaSeleccionada] = useState(initialItem || null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [areasNegocio, setAreasNegocio] = useState([]);
    const [categorias, setCategorias] = useState([]);

    // Estado para filtros
    const [filtros, setFiltros] = useState({
        texto: '',
        area: 'Todas',
        categoria: 'Todas'
    });

    useEffect(() => {
        const cargarNoticias = async () => {
            try {
                setLoading(true);
                setError(null);

                const idUsr = getSessionUserId();

                const [resNoticias, resAreas, resCats] = await Promise.all([
                    api.get(ENDPOINTS.noticiasDashboardByUsuarioId(idUsr)),
                    api.get(ENDPOINTS.areasNegocios),
                    api.get(ENDPOINTS.categorias)
                ]);

                const dataNoticias = resNoticias.data;
                const dataAreas = resAreas.data;
                const dataCats = resCats.data;

                setAreasNegocio(dataAreas);
                setCategorias(dataCats);

                // Transformar datos del backend al formato esperado por el componente
                const noticiasTransformadas = dataNoticias
                    .map(noticia => ({
                        id: noticia.idNoticia || noticia.IdNoticia,
                        titulo: noticia.titulo || noticia.Titulo,
                        resumen: noticia.resumen || noticia.Resumen,
                        contenido: noticia.contenido || noticia.Contenido,
                        fechaPublicacion: noticia.fechaPublicacion || noticia.FechaPublicacion,
                        imagen: noticia.imagenUrl || noticia.ImagenUrl,
                        destacada: noticia.destacada || noticia.Destacada,
                        area: noticia.nombreArea || noticia.NombreArea || "Corporativo",
                        categoria: noticia.nombreCategoria || noticia.NombreCategoria || "General",
                        estado: (noticia.nombreEstado || noticia.NombreEstado || "borrador").toLowerCase(),
                        tipoNoticia: noticia.tipoNoticia || noticia.TipoNoticia || "Baja",
                        autor: noticia.nombreAutor || noticia.NombreAutor || "Experto Corporativo"
                    }))
                    // Filtrar solo noticias publicadas para usuarios normales
                    .filter(n => n.estado === "publicada");

                console.log('=== DEBUG NOTICIAS USUARIO ===');
                console.log('Total noticias desde API:', dataNoticias.length);
                console.log('Noticias después de filtrar:', noticiasTransformadas.length);
                console.log('Muestra de noticias:', noticiasTransformadas.slice(0, 3).map(n => ({ titulo: n.titulo, estado: n.estado })));
                console.log('==============================');

                // Ordenar: Destacadas primero, luego por fecha más reciente
                const ordenadas = [...noticiasTransformadas].sort((a, b) => {
                    // Prioridad por TipoNoticia
                    const niveles = { 'Alta': 1, 'Media': 2, 'Baja': 3 };
                    const nivelA = niveles[a.tipoNoticia] || 3;
                    const nivelB = niveles[b.tipoNoticia] || 3;

                    if (nivelA !== nivelB) return nivelA - nivelB;

                    // Luego por fecha más reciente
                    const fA = new Date(a.fechaPublicacion).getTime();
                    const fB = new Date(b.fechaPublicacion).getTime();
                    return fB - fA;
                });

                setNoticias(ordenadas);
            } catch (e) {
                console.error("Error cargando noticias:", e);
                setError("No se pudieron cargar las noticias. Por favor, intenta nuevamente.");
            } finally {
                setLoading(false);
            }
        };

        cargarNoticias();
    }, []);

    // Handler para cambios en filtros
    const handleFiltrosChange = (e) => {
        const { name, value } = e.target;
        setFiltros(prev => ({ ...prev, [name]: value }));
    };

    // Filtrar noticias según los filtros activos
    const noticiasFiltradas = useMemo(() => {
        return noticias.filter(noticia => {
            // Filtro por texto
            if (filtros.texto) {
                const textoLower = filtros.texto.toLowerCase();
                const matchTexto =
                    noticia.titulo?.toLowerCase().includes(textoLower) ||
                    noticia.resumen?.toLowerCase().includes(textoLower) ||
                    noticia.contenido?.toLowerCase().includes(textoLower);
                if (!matchTexto) return false;
            }

            // Filtro por área
            if (filtros.area !== 'Todas' && noticia.area !== filtros.area) {
                return false;
            }

            // Filtro por categoría
            if (filtros.categoria !== 'Todas' && noticia.categoria !== filtros.categoria) {
                return false;
            }

            return true;
        });
    }, [noticias, filtros]);

    const handleVerDetalle = (noticia) => {
        setNoticiaSeleccionada(noticia);
    };

    const handleCerrarDetalle = () => {
        setNoticiaSeleccionada(null);
    };

    // Modal logic is handled in the main return now


    // Vista de grid de noticias
    return (
        <div className="intra-news-page">
            {/* HEADER */}
            <header className="intra-news-header">
                <div>
                    {onVolver && (
                        <button
                            type="button"
                            onClick={onVolver}
                            style={{
                                marginBottom: '0.75rem',
                                background: '#f1f5f9',
                                border: '1px solid #e2e8f0',
                                color: '#334155',
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.5rem 1rem',
                                borderRadius: '20px',
                                transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#e2e8f0';
                                e.currentTarget.style.color = '#1e293b';
                                e.currentTarget.style.borderColor = '#cbd5e1';
                                e.currentTarget.style.transform = 'translateX(-3px)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = '#f1f5f9';
                                e.currentTarget.style.color = '#334155';
                                e.currentTarget.style.borderColor = '#e2e8f0';
                                e.currentTarget.style.transform = 'translateX(0)';
                            }}
                        >
                            <span style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>←</span>
                            <span>Volver al dashboard</span>
                        </button>
                    )}
                    <h1 className="intra-news-title">Noticias internas</h1>
                </div>
            </header>

            {/* FILTROS */}
            <section className="intra-news-toolbar">
                <div className="intra-news-filters" style={{ width: '100%', display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
                    <div className="intra-filter-group" style={{ flex: '1', minWidth: '200px' }}>
                        <label className="intra-label" htmlFor="filtroTexto">
                            Buscar
                        </label>
                        <input
                            id="filtroTexto"
                            name="texto"
                            type="text"
                            className="intra-input"
                            value={filtros.texto}
                            onChange={handleFiltrosChange}
                            placeholder="Buscar por título, resumen o contenido..."
                        />
                    </div>

                    <div className="intra-filter-group" style={{ width: '180px' }}>
                        <label className="intra-label" htmlFor="filtroArea">
                            Área
                        </label>
                        <select
                            id="filtroArea"
                            name="area"
                            className="intra-select"
                            value={filtros.area}
                            onChange={handleFiltrosChange}
                        >
                            <option value="Todas">Todas</option>
                            {areasNegocio.map((area, idx) => (
                                <option key={area.idAreaNegocio || area.IdAreaNegocio || idx} value={area.nombre || area.Nombre || area.descripcion || area.Descripcion}>
                                    {area.nombre || area.Nombre || area.descripcion || area.Descripcion}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="intra-filter-group" style={{ width: '180px' }}>
                        <label className="intra-label" htmlFor="filtroCategoria">
                            Categoría
                        </label>
                        <select
                            id="filtroCategoria"
                            name="categoria"
                            className="intra-select"
                            value={filtros.categoria}
                            onChange={handleFiltrosChange}
                        >
                            <option value="Todas">Todas</option>
                            {categorias.map((cat, idx) => (
                                <option key={cat.idCategoria || cat.IdCategoria || idx} value={cat.nombre || cat.Nombre}>
                                    {cat.nombre || cat.Nombre}
                                </option>
                            ))}
                        </select>
                    </div>

                </div>
            </section>

            {/* CONTENIDO */}
            <div className="intra-news-content">
                {
                    loading ? (
                        <div className="intra-news-empty" >
                            <p>Cargando noticias...</p>
                        </div>
                    ) : error ? (
                        <div className="intra-news-empty">
                            <p style={{ color: '#ef4444' }}>{error}</p>
                            <button
                                className="intra-button-primary"
                                onClick={() => window.location.reload()}
                                style={{ marginTop: '1rem' }}
                            >
                                Reintentar
                            </button>
                        </div>
                    ) : noticiasFiltradas.length === 0 ? (
                        <div className="intra-news-empty">
                            <p>No se encontraron noticias con los filtros seleccionados.</p>
                        </div>
                    ) : (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                            gap: '1rem',
                        }}>
                            {noticiasFiltradas.map((noticia) => (
                                <article
                                    key={noticia.id}
                                    onClick={() => handleVerDetalle(noticia)}
                                    style={{
                                        backgroundColor: noticia.destacada ? '#fffbeb' : String(noticia.tipoNoticia).toLowerCase() === 'alta' ? '#fef2f2' : '#f0f9ff',
                                        border: `1px solid ${String(noticia.tipoNoticia).toLowerCase() === 'alta' ? '#ef4444' : String(noticia.tipoNoticia).toLowerCase() === 'media' ? '#f59e0b' : '#3b82f6'}`,
                                        borderRadius: '16px',
                                        overflow: 'hidden',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        cursor: 'pointer',
                                        position: 'relative',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        height: '100%',
                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)';
                                        e.currentTarget.style.transform = 'translateY(-6px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                >
                                    {/* Ribbon de urgencia */}
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        right: 0,
                                        width: '40px',
                                        height: '40px',
                                        zIndex: 10,
                                        overflow: 'hidden'
                                    }}>
                                        <div style={{
                                            position: 'absolute',
                                            top: 0,
                                            right: 0,
                                            width: 0,
                                            height: 0,
                                            borderStyle: 'solid',
                                            borderWidth: '0 40px 40px 0',
                                            borderColor: `transparent ${String(noticia.tipoNoticia).toLowerCase() === 'alta' ? '#ef4444' : String(noticia.tipoNoticia).toLowerCase() === 'media' ? '#f59e0b' : '#3b82f6'} transparent transparent`,
                                        }} />
                                        <div style={{
                                            position: 'absolute',
                                            top: '3px',
                                            right: '5px',
                                            color: 'white',
                                            fontSize: '0.8rem',
                                            fontWeight: 'bold',
                                            zIndex: 11
                                        }} title={`Prioridad: ${noticia.tipoNoticia || 'Baja'}`}>
                                            {String(noticia.tipoNoticia).toLowerCase() === 'alta' ? 'A' : String(noticia.tipoNoticia).toLowerCase() === 'media' ? 'M' : 'B'}
                                        </div>
                                    </div>

                                    {/* Imagen */}
                                    <div style={{
                                        width: '100%',
                                        height: '160px',
                                        overflow: 'hidden',
                                        backgroundColor: '#f1f5f9',
                                        position: 'relative'
                                    }}>
                                        {noticia.imagen ? (
                                            <img
                                                src={getImageUrl(noticia.imagen) || PLACEHOLDER_IMAGE}
                                                alt={noticia.titulo}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.parentElement.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; color: #94a3af; font-size: 2rem;">📰</div>';
                                                }}
                                            />
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', justify_content: 'center', width: '100%', height: '100%', color: '#94a3af', fontSize: '2rem' }}>📰</div>
                                        )}
                                        {noticia.destacada && (
                                            <div style={{ position: 'absolute', bottom: '8px', left: '8px', background: '#fbbf24', color: '#78350f', padding: '2px 8px', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 'bold' }}>
                                                Destacada
                                            </div>
                                        )}
                                    </div>

                                    {/* Contenido */}
                                    <div style={{ padding: '1rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '0.75rem' }}>
                                            <span style={{ 
                                                backgroundColor: '#E0E7FF', 
                                                color: '#3730A3', 
                                                padding: '1px 6px', 
                                                borderRadius: '4px', 
                                                fontSize: '0.6rem', 
                                                fontWeight: '700', 
                                                textTransform: 'uppercase',
                                                border: '1px solid #C7D2FE'
                                            }}>
                                                {noticia.area || "Corporativo"}
                                            </span>
                                            {(noticia.categoria && noticia.categoria !== 'General') && (
                                                <span style={{ backgroundColor: getCategoriaStyles(noticia.categoria).bg, color: getCategoriaStyles(noticia.categoria).text, padding: '1px 6px', borderRadius: '4px', fontSize: '0.6rem', fontWeight: '700', textTransform: 'uppercase', border: `1px solid ${getCategoriaStyles(noticia.categoria).border}` }}>
                                                    {noticia.categoria}
                                                </span>
                                            )}
                                        </div>

                                        <h3 style={{ fontSize: '0.95rem', fontWeight: '700', color: '#1e293b', margin: '0 0 0.5rem 0', lineHeight: '1.4', display: '-webkit-box', WebkitLineClamp: '2', WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: '2.8em' }}>
                                            {noticia.titulo}
                                        </h3>

                                        <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '1rem', lineHeight: '1.5', display: '-webkit-box', WebkitLineClamp: '3', WebkitBoxOrient: 'vertical', overflow: 'hidden', flex: 1 }}>
                                            {noticia.resumen || noticia.descripcion || ""}
                                        </p>

                                        <div style={{ paddingTop: '0.75rem', marginTop: 'auto', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.7rem', color: '#94a3b8' }}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>👤 {noticia.autor?.split(' ')[0]}</span>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>📅 {formatFechaCorta(noticia.fechaPublicacion || noticia.fecha)}</span>
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}
            </div >

            {/* MODAL DETALLE NOTICIA */}
            {
                noticiaSeleccionada && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000,
                        padding: '2rem'
                    }} onClick={handleCerrarDetalle}>
                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            width: '100%',
                            maxWidth: '900px',
                            maxHeight: '90vh',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                            overflow: 'hidden'
                        }} onClick={(e) => e.stopPropagation()}>

                            {/* Header Modal */}
                            <div style={{
                                padding: '1.5rem',
                                borderBottom: '1px solid #e2e8f0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                backgroundColor: '#f8fafc'
                            }}>
                                <h2 className="intra-panel-title" style={{ margin: 0, fontSize: '1.25rem' }}>Detalle de Noticia</h2>
                                <button
                                    type="button"
                                    onClick={handleCerrarDetalle}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        fontSize: '2rem',
                                        lineHeight: 1,
                                        color: '#64748b',
                                        cursor: 'pointer'
                                    }}
                                >
                                    ×
                                </button>
                            </div>

                            {/* Contenido Scrollable */}
                            <div style={{ overflowY: 'auto', padding: '2rem' }}>
                                <article className="intra-news-detail">
                                    {noticiaSeleccionada.imagen && (
                                        <div className="intra-news-detail-image" style={{ marginBottom: '1.5rem' }}>
                                            <img
                                                src={getImageUrl(noticiaSeleccionada.imagen) || PLACEHOLDER_IMAGE}
                                                alt={noticiaSeleccionada.titulo}
                                                style={{ width: '100%', borderRadius: '8px', height: 'auto', maxHeight: '450px', objectFit: 'cover' }}
                                                onError={(e) => {
                                                    if (e.target.src !== PLACEHOLDER_IMAGE) {
                                                        e.target.src = PLACEHOLDER_IMAGE;
                                                    }
                                                }}
                                            />
                                        </div>
                                    )}

                                    <div className="intra-news-detail-meta" style={{ marginBottom: '1rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
                                        <span style={{
                                            backgroundColor: '#E0E7FF',
                                            color: '#3730A3',
                                            padding: '4px 12px',
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            fontWeight: '700',
                                            display: 'inline-block',
                                            border: '1px solid #C7D2FE'
                                        }}>
                                            {noticiaSeleccionada.area || "Corporativo"}
                                        </span>
                                        {noticiaSeleccionada.categoria && (
                                            <span style={{
                                                backgroundColor: '#F3F4F6',
                                                color: '#374151',
                                                padding: '4px 12px',
                                                borderRadius: '6px',
                                                fontSize: '0.85rem',
                                                fontWeight: '700',
                                                display: 'inline-block',
                                                border: '1px solid #E5E7EB'
                                            }}>
                                                {noticiaSeleccionada.categoria}
                                            </span>
                                        )}
                                        <span style={{
                                            fontSize: '0.75rem',
                                            fontWeight: '700',
                                            padding: '4px 10px',
                                            borderRadius: '12px',
                                            textTransform: 'uppercase',
                                            backgroundColor: noticiaSeleccionada.tipoNoticia === 'Alta' ? '#FEE2E2' : noticiaSeleccionada.tipoNoticia === 'Media' ? '#FEF3C7' : '#E0F2FE',
                                            color: noticiaSeleccionada.tipoNoticia === 'Alta' ? '#B91C1C' : noticiaSeleccionada.tipoNoticia === 'Media' ? '#B45309' : '#0369A1'
                                        }}>
                                            {noticiaSeleccionada.tipoNoticia}
                                        </span>
                                        <span className="intra-news-card-date" style={{ color: '#64748b', fontSize: '0.9rem' }}>
                                            {formatFechaCorta(noticiaSeleccionada.fechaPublicacion || noticiaSeleccionada.fecha)}
                                        </span>
                                    </div>

                                    <h2 className="intra-news-detail-title" style={{ fontSize: '2rem', color: '#1e293b', marginBottom: '1rem', lineHeight: '1.3' }}>
                                        {noticiaSeleccionada.titulo}
                                    </h2>

                                    {noticiaSeleccionada.resumen && (
                                        <p className="intra-news-detail-excerpt" style={{ fontSize: '1.1rem', color: '#475569', fontStyle: 'italic', marginBottom: '1.5rem', borderLeft: '4px solid #cbd5e1', paddingLeft: '1rem' }}>
                                            {noticiaSeleccionada.resumen}
                                        </p>
                                    )}

                                    <div className="intra-news-detail-content" style={{ fontSize: '1rem', lineHeight: '1.8', color: '#334155', whiteSpace: 'pre-wrap' }}>
                                        {noticiaSeleccionada.contenido || noticiaSeleccionada.descripcion || "Sin contenido disponible"}
                                    </div>

                                    {noticiaSeleccionada.autor && (
                                        <div className="intra-news-detail-author" style={{ marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0', color: '#64748b' }}>
                                            <strong>Autor:</strong> {noticiaSeleccionada.autor}
                                        </div>
                                    )}
                                </article>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default NoticiasUsuario;
